/**
 * Trader overlay UI — buy items from the trader's stock, sell your own items.
 */
import type { FederatedPointerEvent } from 'pixi.js';
import { Container, Graphics, Text } from 'pixi.js';
import { draw3DButton, drawBevelPanel, drawHeaderBand, drawInsetRect, GUGI_SHADOW, buildCloseButton, buildPanelTitleBand, type OverlayResult } from './PanelUtils';
import { ACCENT, ACCENT_DIM, ACCENT_DARK } from './LayoutConstants';
import {
  INV_CELL, INV_GAP,
  buildUpgradeCell, buildWeaponCell,
  createUpgradeTooltip, createWeaponTooltip, positionTooltip,
} from './InventoryUI';
import { getUpgradeById, type UpgradeDef } from '../upgrades/UpgradeData';
import { getWeaponById, type WeaponDef } from '../upgrades/WeaponData';
import { type ScoredItem } from '../upgrades/Quality';
import { type GameStateManager } from '../states/GameStateManager';
import { isWeaponItem, getItemBuyPrice, getItemSellPrice, getItemTier } from '../upgrades/TraderSystem';
import { createScrollBar, SCROLLBAR_WIDTH } from './ScrollBar';
import { attachScrollDrag } from './ScrollablePanel';
import { trackTraderBuy, trackTraderSell } from '../achievements/Hooks';

const PANEL_W = 740;
const PANEL_H = 540;
const GRID_COLS = 4;
const SELL_COLS = 4;
const PAD = 14;

function formatTime(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Trader overlay uses a custom `updateTimer` hook instead of the standard
 * `update(dt)` — it only needs wall-clock refresh, not per-frame work.
 */
export interface TraderOverlayResult extends Omit<OverlayResult, 'update'> {
  /** Call every frame to update the countdown timer */
  updateTimer: () => void;
}

export function buildTraderOverlay(
  manager: GameStateManager,
  screenW: number,
  screenH: number,
  onClose: () => void,
  onTransaction: () => void,
): TraderOverlayResult {
  const root = new Container();
  let tooltip: Container | null = null;

  const hideTooltip = () => {
    if (tooltip) { root.removeChild(tooltip); tooltip.destroy({ children: true }); tooltip = null; }
  };

  const showTooltip = (item: ScoredItem, cellX: number, cellY: number) => {
    hideTooltip();
    const weapon = isWeaponItem(item);
    const def = weapon ? getWeaponById(item.id) : getUpgradeById(item.id);
    if (!def) return;
    const { tip, width, height } = weapon
      ? createWeaponTooltip(def as WeaponDef, item)
      : createUpgradeTooltip(def as UpgradeDef, item);
    positionTooltip(tip, height, width, cellX, cellY, screenW, screenH);
    root.addChild(tip);
    tooltip = tip;
  };

  // ─── Dimmed backdrop ────────────────────────────────────
  const backdrop = new Graphics();
  backdrop.rect(0, 0, screenW, screenH);
  backdrop.fill({ color: 0x000000, alpha: 0.6 });
  backdrop.eventMode = 'static';
  backdrop.cursor = 'default';
  backdrop.on('pointerdown', (e: FederatedPointerEvent) => e.stopPropagation());
  root.addChild(backdrop);

  // ─── Main panel ─────────────────────────────────────────
  const panelX = Math.round((screenW - PANEL_W) / 2);
  const panelY = Math.round((screenH - PANEL_H) / 2);

  const panel = new Container();
  panel.position.set(panelX, panelY);
  root.addChild(panel);

  const bg = new Graphics();
  drawBevelPanel(bg, 0, 0, PANEL_W, PANEL_H, { fill: 0x0c0e14, border: ACCENT_DIM, radius: 6, accentEdge: ACCENT, rivets: true });
  panel.addChild(bg);

  // ─── Header ─────────────────────────────────────────────
  buildPanelTitleBand(panel, PANEL_W, 'TRADER', ACCENT, ACCENT_DARK, PAD);

  // Credits display
  const creditsText = new Text({
    text: `Cr  ${manager.money.toLocaleString()}`,
    style: { fontFamily: 'Offside', fontSize: 15, fill: 0xffdd44 },
  });
  creditsText.anchor.set(0.5, 0);
  creditsText.position.set(PANEL_W / 2, 10);
  panel.addChild(creditsText);

  // Timer
  const timerText = new Text({
    text: `Restock: ${formatTime(manager.trader.timeUntilRefresh())}`,
    style: { fontFamily: 'Offside', fontSize: 13, fill: 0x667788 },
  });
  timerText.anchor.set(1, 0);
  timerText.position.set(PANEL_W - PAD - 40, 12);
  panel.addChild(timerText);

  // Close button
  buildCloseButton(panel, PANEL_W - 40, 5, ACCENT, ACCENT_DARK, () => { hideTooltip(); onClose(); });

  // ─── Content area ───────────────────────────────────────
  const contentY = 40;
  const contentH = PANEL_H - contentY - PAD;
  const halfW = Math.floor((PANEL_W - PAD * 3) / 2);

  // ── Left half: FOR SALE ─────────────────────────────────
  const leftX = PAD;
  const leftPanel = new Container();
  leftPanel.position.set(leftX, contentY);
  panel.addChild(leftPanel);

  const saleBand = new Graphics();
  drawHeaderBand(saleBand, 0, 0, halfW, ACCENT_DARK, 0x0c0e14, 22);
  leftPanel.addChild(saleBand);
  leftPanel.addChild(new Text({ text: 'FOR SALE', style: { fontFamily: 'Gugi', fontSize: 12, fill: 0x8899aa, dropShadow: GUGI_SHADOW } })).position.set(4, 4);

  const stock = manager.trader.stock;
  const buyGridY = 28;
  const buyAreaH = contentH - buyGridY - 4;
  const buyRowH = INV_CELL + INV_GAP + 16;

  // Scrollable content for buy side (mirrors sell side).
  const buyContent = new Container();
  buyContent.eventMode = 'static';
  buyContent.position.set(0, buyGridY);
  leftPanel.addChild(buyContent);

  const buyViewW = halfW - SCROLLBAR_WIDTH - 4;
  const buyMask = new Graphics();
  buyMask.rect(0, buyGridY, buyViewW, buyAreaH);
  buyMask.fill(0xffffff);
  leftPanel.addChild(buyMask);
  buyContent.mask = buyMask;

  const buyTotalRows = Math.ceil(stock.length / GRID_COLS);
  const buyTotalH = buyTotalRows * buyRowH;

  const buyHit = new Graphics();
  buyHit.rect(0, 0, buyViewW, Math.max(buyAreaH, buyTotalH));
  buyHit.fill({ color: 0x000000, alpha: 0.001 });
  buyHit.eventMode = 'static';
  buyContent.addChildAt(buyHit, 0);

  let buyScrollBarHandle: ReturnType<typeof createScrollBar> | null = null;
  let buyScrollY = 0;
  const buyMaxScroll = Math.max(0, buyTotalH - buyAreaH);

  const buyScrollDrag = attachScrollDrag({
    hitArea: buyContent,
    onDelta: (dy: number) => {
      if (buyMaxScroll <= 0) return;
      buyScrollY = Math.max(0, Math.min(buyMaxScroll, buyScrollY + dy));
      buyContent.y = buyGridY - buyScrollY;
      buyScrollBarHandle?.setFraction(buyScrollY / buyMaxScroll);
    },
  });

  for (let i = 0; i < stock.length; i++) {
    const item = stock[i];
    const col = i % GRID_COLS;
    const row = Math.floor(i / GRID_COLS);
    const cx = col * (INV_CELL + INV_GAP);
    const cy = row * buyRowH;

    if (!item) {
      // Empty slot (already purchased)
      const empty = new Graphics();
      drawInsetRect(empty, 0, 0, INV_CELL, INV_CELL, 4, 0x12161e, 0x1a2030, 0.3);
      empty.position.set(cx, cy);
      buyContent.addChild(empty);
      continue;
    }

    const weapon = isWeaponItem(item);
    const def = weapon ? getWeaponById(item.id) : getUpgradeById(item.id);
    if (!def) continue;

    const cell = weapon
      ? buildWeaponCell(def as WeaponDef, item)
      : buildUpgradeCell(def as UpgradeDef, item);
    cell.position.set(cx, cy);
    buyContent.addChild(cell);

    const buyPrice = getItemBuyPrice(item);
    const canAfford = manager.money >= buyPrice;

    const priceText = new Text({
      text: `${buyPrice} Cr`,
      style: { fontFamily: 'Offside', fontSize: 11, fill: canAfford ? 0xffdd44 : 0xff4444 },
    });
    priceText.anchor.set(0.5, 0);
    priceText.position.set(cx + INV_CELL / 2, cy + INV_CELL + 1);
    buyContent.addChild(priceText);

    // Click to buy
    cell.eventMode = 'static';
    cell.cursor = canAfford ? 'pointer' : 'not-allowed';

    const idx = i;
    const absCellX = panelX + leftX + cx;
    const absCellY = panelY + contentY + buyGridY + cy;

    cell.on('pointerenter', () => showTooltip(item, absCellX, absCellY));
    cell.on('pointerleave', () => hideTooltip());

    if (canAfford) {
      cell.on('pointertap', () => {
        if (buyScrollDrag.wasDragging()) return;
        hideTooltip();
        manager.money -= buyPrice;
        manager.trader.removeFromStock(idx);
        if (weapon) {
          manager.upgrades.addWeaponToInventory(item);
        } else {
          manager.upgrades.addToInventory(item);
        }
        trackTraderBuy(manager.achievements, buyPrice, item.score);
        manager.save();
        onTransaction();
      });
    }
  }

  if (buyMaxScroll > 0) {
    buyScrollBarHandle = createScrollBar({
      trackHeight: buyAreaH,
      contentHeight: buyTotalH,
      visibleHeight: buyAreaH,
      onChange: (frac) => {
        buyScrollY = frac * buyMaxScroll;
        buyContent.y = buyGridY - buyScrollY;
      },
    });
    buyScrollBarHandle.container.position.set(halfW - SCROLLBAR_WIDTH, buyGridY);
    leftPanel.addChild(buyScrollBarHandle.container);
  }

  // ── Right half: YOUR ITEMS (SELL) ───────────────────────
  const rightX = PAD * 2 + halfW;
  const rightPanel = new Container();
  rightPanel.position.set(rightX, contentY);
  panel.addChild(rightPanel);

  const yourBand = new Graphics();
  drawHeaderBand(yourBand, 0, 0, halfW, ACCENT_DARK, 0x0c0e14, 22);
  rightPanel.addChild(yourBand);
  rightPanel.addChild(new Text({ text: 'YOUR ITEMS', style: { fontFamily: 'Gugi', fontSize: 12, fill: 0x8899aa, dropShadow: GUGI_SHADOW } })).position.set(4, 4);

  // ── Sell Junk filter bar ────────────────────────────────
  // Score-based junk filter: "max tier" (unchanged) and "max score" threshold.
  // Items at or below both thresholds are considered junk.
  let filterMaxTier = 1;
  let filterMaxScore = 30;

  // Combine upgrades + weapons
  const allPlayerItems: { item: ScoredItem; weapon: boolean }[] = [
    ...manager.upgrades.inventory.map(item => ({ item, weapon: false })),
    ...manager.upgrades.weaponInventory.map(item => ({ item, weapon: true })),
  ];

  const isJunk = (item: ScoredItem): boolean =>
    getItemTier(item) <= filterMaxTier && item.score <= filterMaxScore;

  const getJunkSummary = (): { count: number; total: number } => {
    let count = 0, total = 0;
    for (const { item } of allPlayerItems) {
      if (isJunk(item)) { count++; total += getItemSellPrice(item); }
    }
    return { count, total };
  };

  const filterBar = new Container();
  filterBar.position.set(0, 26);
  rightPanel.addChild(filterBar);

  const filterBg = new Graphics();
  filterBg.roundRect(0, 0, halfW, 68, 3);
  filterBg.fill({ color: 0x0a0c12, alpha: 0.8 });
  filterBg.stroke({ color: ACCENT_DIM, width: 1, alpha: 0.3 });
  filterBar.addChild(filterBg);

  // Tier row
  const tierLabel = new Text({ text: 'Max Tier:', style: { fontFamily: 'Offside', fontSize: 11, fill: 0x667788 } });
  tierLabel.position.set(4, 4);
  filterBar.addChild(tierLabel);

  const tierBtns: { bg: Graphics; label: Text }[] = [];
  const tierBtnW = 24;
  const tierBtnH = 16;
  const tierStartX = 70;

  for (let t = 1; t <= 10; t++) {
    const btn = new Container();
    const bg = new Graphics();
    const active = t <= filterMaxTier;
    bg.roundRect(0, 0, tierBtnW, tierBtnH, 2);
    bg.fill(active ? ACCENT : 0x1a1e28);
    bg.stroke({ color: active ? ACCENT : 0x333844, width: 1 });
    btn.addChild(bg);
    const lbl = new Text({ text: `${t}`, style: { fontFamily: 'Offside', fontSize: 10, fill: active ? 0xffffff : 0x556677 } });
    lbl.anchor.set(0.5); lbl.position.set(tierBtnW / 2, tierBtnH / 2);
    btn.addChild(lbl);
    btn.position.set(tierStartX + (t - 1) * (tierBtnW + 2), 2);
    btn.eventMode = 'static'; btn.cursor = 'pointer';
    tierBtns.push({ bg, label: lbl });

    const tier = t;
    btn.on('pointerdown', () => {
      filterMaxTier = tier;
      updateFilterUI();
    });
    filterBar.addChild(btn);
  }

  // Score threshold row — each button maps to an upper score bound.
  const scoreLabel = new Text({ text: 'Max Score:', style: { fontFamily: 'Offside', fontSize: 11, fill: 0x667788 } });
  scoreLabel.position.set(4, 24);
  filterBar.addChild(scoreLabel);

  const SCORE_THRESHOLDS: { label: string; max: number; color: number }[] = [
    { label: '\u226430', max: 30, color: 0xaaaaaa },
    { label: '\u226460', max: 60, color: 0x44cc44 },
    { label: '\u226485', max: 85, color: 0x4488ff },
    { label: '\u226495', max: 95, color: 0xaa55ff },
  ];
  const scoreBtns: { bg: Graphics; label: Text }[] = [];
  const scoreBtnW = 36;
  const scoreBtnH = 16;
  const scoreStartX = 70;

  for (let i = 0; i < SCORE_THRESHOLDS.length; i++) {
    const entry = SCORE_THRESHOLDS[i]!;
    const btn = new Container();
    const bg = new Graphics();
    const active = entry.max === filterMaxScore;
    bg.roundRect(0, 0, scoreBtnW, scoreBtnH, 2);
    bg.fill(active ? entry.color : 0x1a1e28);
    bg.stroke({ color: active ? entry.color : 0x333844, width: 1 });
    btn.addChild(bg);
    const lbl = new Text({ text: entry.label, style: { fontFamily: 'Offside', fontSize: 10, fill: active ? 0xffffff : 0x556677 } });
    lbl.anchor.set(0.5); lbl.position.set(scoreBtnW / 2, scoreBtnH / 2);
    btn.addChild(lbl);
    btn.position.set(scoreStartX + i * (scoreBtnW + 3), 22);
    btn.eventMode = 'static'; btn.cursor = 'pointer';
    scoreBtns.push({ bg, label: lbl });

    btn.on('pointerdown', () => {
      filterMaxScore = entry.max;
      updateFilterUI();
    });
    filterBar.addChild(btn);
  }

  // Sell Junk button
  const sellJunkBtn = new Container();
  const sellJunkBg = new Graphics();
  const sellJunkW = halfW - 8;
  const sellJunkH = 22;
  sellJunkBtn.position.set(4, 43);
  filterBar.addChild(sellJunkBtn);

  const sellJunkLabel = new Text({
    text: 'SELL JUNK',
    style: { fontFamily: 'Gugi', fontSize: 11, fill: 0xffffff, dropShadow: GUGI_SHADOW },
  });
  sellJunkLabel.anchor.set(0.5); sellJunkLabel.position.set(sellJunkW / 2, sellJunkH / 2);

  sellJunkBtn.addChild(sellJunkBg);
  sellJunkBtn.addChild(sellJunkLabel);
  sellJunkBtn.eventMode = 'static';

  sellJunkBtn.on('pointerdown', () => {
    const junkItems = allPlayerItems.filter(({ item }) => isJunk(item));
    if (junkItems.length === 0) return;
    hideTooltip();
    let totalPrice = 0;
    for (const { item, weapon } of junkItems) {
      totalPrice += getItemSellPrice(item);
      if (weapon) {
        manager.upgrades.removeWeaponFromInventory(item);
      } else {
        manager.upgrades.removeFromInventory(item);
      }
    }
    manager.money += totalPrice;
    manager.save();
    onTransaction();
  });

  // Overlay highlights for junk items (collected during grid build, updated by filter)
  const junkOverlays: { overlay: Graphics; item: ScoredItem }[] = [];

  const updateFilterUI = () => {
    // Update tier buttons
    for (let t = 0; t < tierBtns.length; t++) {
      const active = t + 1 <= filterMaxTier;
      const { bg, label } = tierBtns[t]!;
      bg.clear();
      bg.roundRect(0, 0, tierBtnW, tierBtnH, 2);
      bg.fill(active ? ACCENT : 0x1a1e28);
      bg.stroke({ color: active ? ACCENT : 0x333844, width: 1 });
      label.style.fill = active ? 0xffffff : 0x556677;
    }
    // Update score buttons
    for (let i = 0; i < scoreBtns.length; i++) {
      const entry = SCORE_THRESHOLDS[i]!;
      const active = entry.max === filterMaxScore;
      const { bg, label } = scoreBtns[i]!;
      bg.clear();
      bg.roundRect(0, 0, scoreBtnW, scoreBtnH, 2);
      bg.fill(active ? entry.color : 0x1a1e28);
      bg.stroke({ color: active ? entry.color : 0x333844, width: 1 });
      label.style.fill = active ? 0xffffff : 0x556677;
    }
    // Update junk overlays
    for (const { overlay, item } of junkOverlays) {
      overlay.visible = isJunk(item);
    }
    // Update sell junk button
    const { count, total } = getJunkSummary();
    const hasJunk = count > 0;
    sellJunkBg.clear();
    draw3DButton(sellJunkBg, sellJunkW, sellJunkH, hasJunk ? 0x226622 : 0x1a1e28, { radius: 3, depth: 2 });
    sellJunkLabel.text = hasJunk ? `SELL JUNK  (${count} items — ${total} Cr)` : 'SELL JUNK  (none)';
    sellJunkLabel.style.fill = hasJunk ? 0xffffff : 0x556677;
    sellJunkBtn.cursor = hasJunk ? 'pointer' : 'default';
  };

  // Initial draw of sell junk button
  updateFilterUI();

  // ── Sell item grid ──────────────────────────────────────
  const sellGridY = 98;
  const sellAreaH = contentH - sellGridY - 4;

  // Scrollable content for sell side
  const sellContent = new Container();
  sellContent.eventMode = 'static';
  sellContent.position.set(0, sellGridY);
  rightPanel.addChild(sellContent);
  const sellViewW = halfW - SCROLLBAR_WIDTH - 4;
  const sellMask = new Graphics();
  sellMask.rect(0, sellGridY, sellViewW, sellAreaH);
  sellMask.fill(0xffffff);
  rightPanel.addChild(sellMask);
  sellContent.mask = sellMask;

  // Transparent hit-area inside sellContent — cell events bubble to sellContent.
  const sellHit = new Graphics();
  sellHit.eventMode = 'static';
  sellContent.addChildAt(sellHit, 0);

  let cellIdx = 0;
  for (const { item, weapon } of allPlayerItems) {
    const def = weapon ? getWeaponById(item.id) : getUpgradeById(item.id);
    if (!def) continue;

    const col = cellIdx % SELL_COLS;
    const row = Math.floor(cellIdx / SELL_COLS);
    const cx = col * (INV_CELL + INV_GAP);
    const cy = row * (INV_CELL + INV_GAP + 16);

    const cell = weapon
      ? buildWeaponCell(def as WeaponDef, item)
      : buildUpgradeCell(def as UpgradeDef, item);
    cell.position.set(cx, cy);
    sellContent.addChild(cell);

    // Junk highlight overlay
    const overlay = new Graphics();
    overlay.roundRect(0, 0, INV_CELL, INV_CELL, 4);
    overlay.fill({ color: 0x44cc44, alpha: 0.15 });
    overlay.position.set(cx, cy);
    overlay.visible = isJunk(item);
    overlay.eventMode = 'none';
    sellContent.addChild(overlay);
    junkOverlays.push({ overlay, item });

    const sellPrice = getItemSellPrice(item);
    const priceText = new Text({
      text: `${sellPrice} Cr`,
      style: { fontFamily: 'Offside', fontSize: 11, fill: 0x44cc44 },
    });
    priceText.anchor.set(0.5, 0);
    priceText.position.set(cx + INV_CELL / 2, cy + INV_CELL + 1);
    sellContent.addChild(priceText);

    cell.eventMode = 'static';
    cell.cursor = 'pointer';

    const absCellX = panelX + rightX + cx;
    const absCellY = panelY + contentY + sellGridY + cy;

    cell.on('pointerenter', () => showTooltip(item, absCellX, absCellY));
    cell.on('pointerleave', () => hideTooltip());

    cell.on('pointertap', () => {
      if (sellScrollDrag.wasDragging()) return;
      hideTooltip();
      manager.money += sellPrice;
      if (weapon) {
        manager.upgrades.removeWeaponFromInventory(item);
      } else {
        manager.upgrades.removeFromInventory(item);
      }
      trackTraderSell(manager.achievements, sellPrice);
      manager.save();
      onTransaction();
    });

    cellIdx++;
  }

  if (allPlayerItems.length === 0) {
    const empty = new Text({
      text: 'No items to sell.',
      style: { fontFamily: 'Offside', fontSize: 13, fill: 0x445566 },
    });
    empty.position.set(4, 4);
    sellContent.addChild(empty);
  }

  // Size sell hit-area to match content so wheel/pointer fire across gaps.
  const totalSellH = Math.ceil(cellIdx / SELL_COLS) * (INV_CELL + INV_GAP + 16);
  const maxScroll = Math.max(0, totalSellH - sellAreaH);
  sellHit.clear();
  sellHit.rect(0, sellGridY, sellViewW, Math.max(sellAreaH, totalSellH));
  sellHit.fill({ color: 0x000000, alpha: 0.001 });

  let sellScrollY = 0;
  const sellScrollBar = createScrollBar({
    trackHeight: sellAreaH,
    contentHeight: totalSellH,
    visibleHeight: sellAreaH,
    onChange: (frac) => {
      sellScrollY = frac * maxScroll;
      sellContent.y = sellGridY - sellScrollY;
    },
  });
  sellScrollBar.container.position.set(halfW - SCROLLBAR_WIDTH, sellGridY);
  rightPanel.addChild(sellScrollBar.container);

  const sellScrollDrag = attachScrollDrag({
    hitArea: sellContent,
    onDelta: (dy: number) => {
      if (maxScroll <= 0) return;
      sellScrollY = Math.max(0, Math.min(maxScroll, sellScrollY + dy));
      sellContent.y = sellGridY - sellScrollY;
      sellScrollBar.setFraction(sellScrollY / maxScroll);
    },
  });

  // ─── Divider line ───────────────────────────────────────
  const divider = new Graphics();
  divider.moveTo(PAD + halfW + PAD / 2, contentY);
  divider.lineTo(PAD + halfW + PAD / 2, contentY + contentH);
  divider.stroke({ color: ACCENT_DIM, width: 1, alpha: 0.4 });
  panel.addChild(divider);

  // ─── Timer update function ──────────────────────────────
  // Only reassign Text.text when the rendered string actually changes —
  // otherwise PixiJS re-lays out glyphs on every frame even when the
  // value is unchanged.
  const updateTimer = () => {
    const remaining = manager.trader.timeUntilRefresh();
    const newTimer = `Restock: ${formatTime(remaining)}`;
    if (timerText.text !== newTimer) timerText.text = newTimer;
    const newCredits = `Cr  ${manager.money.toLocaleString()}`;
    if (creditsText.text !== newCredits) creditsText.text = newCredits;
  };

  const dispose = () => {
    buyScrollDrag.destroy();
    sellScrollDrag.destroy();
  };

  return { container: root, updateTimer, dispose };
}
